/**
 * Dictionnaires de l'admin — anglais et français.
 *
 * ⚠️ **`en` est la référence, et le typage suffit à garantir la parité.**
 * `fr` est annoté `Translations`, c'est-à-dire la forme de `en` : une clé
 * oubliée, une clé en trop ou un scope mal orthographié sont des erreurs de
 * `tsc`. C'est ce qui remplace le script `i18n:check` de wargame-app — il n'y
 * a rien à lancer, `bun run check` le fait déjà.
 *
 * ⚠️ **Pas de `as const` sur `en`.** Les valeurs doivent s'élargir en `string` :
 * figées en littéraux, `fr` devrait répéter *mot pour mot* les phrases
 * anglaises pour satisfaire le type. Les clés, elles, restent exactes.
 *
 * Conventions, reprises de wargame-app :
 *
 * - un **scope** par écran ou par domaine, des clés en `kebab-case` ;
 * - `a11y` est à part : il ne contient que des libellés d'accessibilité,
 *   jamais affichés à l'écran. Une chaîne visible n'y a pas sa place, et
 *   inversement ;
 * - les noms de langue (`locales`) sont des **autonymes** : « Français » se dit
 *   Français dans les deux dictionnaires, c'est voulu — on choisit une langue
 *   dans cette langue.
 */
export const en = {
  global: {
    "app-name": "Wargame Admin",
    "sign-out": "Sign out",
    /* Porté par `AccountBadge`, partagé par la modération et les comptes. */
    "account-deleted": "Deleted account",
  },

  signIn: {
    title: "Sign in",
    subtitle: "Restricted to accounts with the administrator role.",
    email: "Email",
    password: "Password",
    submit: "Sign in",
    submitting: "Signing in…",
    "in-progress": "Signing in",
    "failed-title": "Could not sign in",
    "denied-title": "Access denied",
    "denied-description": "This account does not have the administrator role.",
    tagline: "The command post for WargameApp.",
    "feature-reports": "Work through the report queue",
    "feature-events": "Review, edit and cancel events",
    "feature-accounts": "Create accounts and manage roles",
    footnote:
      "Internal console — nothing is stored here, everything comes from the WargameApp API.",
  },

  dashboard: {
    "signed-in-as": "Signed in as",
    "session-error": "Could not read the session",
    "load-error": "Could not load the dashboard",
    "panel-error": "Could not be loaded.",

    // Poste de garde.
    "watch-eyebrow": "Needs a decision",
    "pending-label": "reports waiting",
    "open-queue": "Open the queue",
    "queue-clear": "Queue clear",
    "queue-clear-hint": "Nothing is waiting for a decision.",
    "queue-unreadable": "The queue could not be read.",
    "oldest-since": "Oldest has been waiting {{duration}}",
    "sla-label": "Time against the {{hours}}-hour target",
    "sla-target": "{{hours}} h target",
    "sla-fresh": "On time",
    "sla-soon": "Due soon",
    "sla-late": "Overdue",

    // Les quatre chiffres de la plateforme.
    "pulse-eyebrow": "Across the platform",
    "tile-accounts": "Accounts",
    "tile-events": "Events",
    "tile-upcoming": "Upcoming games",
    "tile-feed": "Feed posts",
    "tile-handled": "Reports handled",
    "hint-last-signup": "Latest signup {{when}}",
    "hint-open-events": "{{count}} still taking players",
    "hint-next-game": "Next one {{when}}",
    "hint-feed-pending": "{{count}} waiting for review",
    "hint-dismissed": "{{count}} dismissed",

    // Les trois panneaux.
    "see-all": "See all",
    "see-all-queue": "See all — moderation queue",
    "see-all-events": "See all — events",
    "see-all-accounts": "See all — accounts",
    "see-all-feed": "See all — feed queue",
    "queue-title": "Oldest first",
    "queue-empty": "No report is waiting.",
    "reported-by": "Reported by",
    "feed-title": "Posts waiting for review",
    "feed-empty": "No post is waiting.",
    "upcoming-title": "Next games",
    "upcoming-empty": "No game is scheduled.",
    "accounts-title": "New accounts",
    "accounts-empty": "No account yet.",

    // Tendances — les six graphiques.
    "stats-eyebrow": "Trends",
    "granularity-day": "Daily",
    "granularity-month": "Monthly",
    "granularity-year": "Yearly",
    "chart-empty": "Nothing recorded over this window.",
    "chart-accounts-title": "Signups",
    "chart-accounts-caption": "Accounts created per period.",
    "chart-events-title": "Events created",
    "chart-events-caption": "Games opened per period.",
    "chart-reports-title": "Reports filed",
    "chart-reports-caption": "The incoming load, whatever was decided since.",
    "chart-resolution-title": "Time to decision",
    "chart-resolution-caption":
      "Median wait before a verdict, against the 24-hour target.",
    "chart-reasons-title": "Reports by reason",
    "chart-reasons-caption":
      "Where moderation effort goes, over the whole window.",
    "chart-fill-title": "Seats filled",
    "chart-fill-caption": "Share of offered seats that find a taker.",
  },

  accounts: {
    "search-placeholder": "Search by email or username",
    "search-error": "Could not run the search",
    "list-error": "Could not load the accounts",
    "follows-error": "Could not load the follows",
    "detail-error": "Could not load the account",
    total: "{{count}} accounts",
    "page-of": "Page {{page}} of {{pages}}",
    previous: "Previous",
    next: "Next",
    "sort-created": "Newest",
    "sort-username": "Username",
    "order-asc": "Ascending",
    "order-desc": "Descending",
    unverified: "Unverified",

    "empty-title": "No account",
    "empty-filtered": "No account matches these filters.",
    "empty-none": "The database holds no account yet.",
    "error-title": "The list could not be loaded",
    "error-description": "The server did not answer. Try again in a moment.",

    "missing-title": "Account not found",
    "missing-description":
      "This address matches no account — it may have been deleted since.",
    "back-to-list": "Back to the list",
    registered: "Registered on {{date}}",
    profile: "Public profile",
    "profile-none": "This account has no public profile.",
    "no-bio": "No bio.",

    armies: "Armies",
    "armies-empty": "No army declared.",
    "events-created": "Events created",
    "events-created-empty": "Has organised nothing.",
    "events-joined": "Events joined",
    "events-joined-empty": "Has joined nothing organised by someone else.",
    "events-error": "Could not load the events of this account",
    "events-truncated": "and {{count}} more — search them from Events.",
    "event-no-date": "no date",
    "reports-filed": "Reports filed",
    "reports-received": "Reports received",

    followed: "Following",
    "followed-empty": "Follows nobody.",
    followers: "Followers",
    "followers-empty": "Nobody follows this account.",
  },

  /**
   * Chercher autour d'un point — partagé par la liste des comptes et celle des
   * événements, qui prennent le même trio `lat`/`lng`/`radius`.
   *
   * ⚠️ Un scope à part **parce que deux écrans s'en servent** : laissé dans
   * `accounts`, il aurait fallu que les événements aillent y piocher, ou qu'ils
   * en recopient les phrases — et la seconde copie aurait dérivé.
   */
  geo: {
    open: "Search around a place",
    active: "Around {{place}} · {{radius}} km",
    point: "a point",
    note: "Addresses come from Photon, an OpenStreetMap search engine — only coordinates ever reach the server.",
    address: "Place",
    "address-placeholder": "Lyon, 12 rue…, 69003",
    "address-empty": "No place matches.",
    "address-count": "{{count}} places suggested",
    radius: "Radius (km)",
    clear: "Clear",
    distance: "{{km}} km",
  },

  events: {
    "search-placeholder": "Search by name or organiser",
    "list-error": "Could not load the events",
    "detail-error": "Could not load the event",
    total: "{{count}} events",
    "page-of": "Page {{page}} of {{pages}}",
    previous: "Previous",
    next: "Next",

    "filter-all": "All",
    "filter-hint": "Filters, sorting and paging are applied by the server.",
    "status-open": "Open",
    "status-complete": "Full",
    "status-close": "Finished",

    "sort-date": "Game date",
    "sort-created": "Newest",
    "sort-name": "Name",
    "sort-distance": "Distance",
    "sort-distance-hint": "Pick a place to sort by distance.",
    "order-asc": "Ascending",
    "order-desc": "Descending",

    "no-date": "No date set",
    "no-location": "No place",
    "places-filled": "{{filled}} / {{places}} seats",
    "pending-count": "{{count}} pending",

    "empty-title": "No event",
    "empty-filtered": "No event matches these filters.",
    "empty-none": "No event is listed.",
    "error-title": "The list could not be loaded",
    "error-description": "The server did not answer. Try again in a moment.",

    "missing-title": "Event not found",
    "missing-description":
      "This address matches no event — it may have been deleted since.",
    "back-to-list": "Back to the list",

    "organised-by": "Organised by",
    "created-on": "created on {{date}}",
    "no-description": "No description.",
    points: "{{count}} points",
    "subtype-solo": "Solo",
    "subtype-duo": "Duo",
    "subtype-team": "Team",
    "level-beginner": "Beginner",
    "level-intermediate": "Intermediate",
    "level-advanced": "Advanced",
    "level-pro": "Pro",
    "finality-equality": "Draw",
    "finality-abandoned": "Abandoned",

    "counter-places": "Seats",
    "counter-participants": "Confirmed",
    "counter-pending": "Applicants",
    "counter-winners": "Winners",
    "counter-total": "People involved",

    location: "Place",
    "location-unknown": "No place recorded for this event.",
    "location-public": "Public address",
    "location-hidden": "Address hidden",
    "location-hidden-note":
      "The organiser chose not to publish this address. It is shown here because administration routes return it raw — it stays hidden everywhere else.",
    coordinates: "{{lat}}, {{lng}}",
    "distance-from-centre": "{{km}} km from the searched place",

    participants: "Confirmed players",
    "participants-empty": "Nobody has been validated yet.",
    applicants: "Applicants",
    "applicants-empty": "No pending application.",
    "no-army": "no army",
    winner: "Winner",
    ghosted: "No-show",

    conversation: "Group conversation",
    "conversation-none": "This event has no conversation thread.",
    "conversation-exists": "Thread #{{id}} is attached to this event.",
  },

  reports: {
    "open-post": "Open the post",
    "status-pending": "Pending",
    "status-actioned": "Upheld",
    "status-dismissed": "Dismissed",
    "help-open": "How verdict and sanction work",
    "target-message": "Message",
    "target-account": "Account",
    "target-account-label": "Reported account",
    "target-event": "Event",
    "target-post": "Post",
    "target-comment": "Comment",
    "reason-spam": "Spam",
    "reason-harassment": "Harassment",
    "reason-inappropriate-content": "Inappropriate content",
    "reason-impersonation": "Impersonation",
    "reason-child-safety": "Child safety",
    "reason-other": "Other",
    "reported-by": "Reported by",
    "no-content": "The reported content was not captured.",
    "empty-title": "Nothing to review",
    "empty-pending":
      "The queue is clear. Reports land here as players flag content.",
    "empty-closed": "No report carries this status yet.",
    "error-title": "The queue could not be loaded",
    "error-description":
      "The server did not return the report queue. Try again in a moment.",
    retry: "Try again",
    "load-error": "Could not load the report queue",

    verdict: "Verdict",
    "verdict-note":
      "A verdict only files the report: nothing is deleted, and nobody is notified — neither the reporter nor the person reported.",
    dismiss: "Dismiss",
    uphold: "Uphold",
    "requalify-to-actioned": "Uphold instead",
    "requalify-to-dismissed": "Dismiss instead",
    "reviewed-by": "Reviewed by",
    "no-reopen":
      "A closed report cannot go back to the pending queue — only to the other verdict.",
    resolving: "Saving…",
    resolved: "Verdict saved",
    "resolve-error": "Could not save the verdict",

    "context-open": "See the conversation",
    "context-title": "Around the reported message",
    "context-close": "Close the conversation",
    "context-direct": "Direct conversation.",
    "context-group": "Group conversation.",
    "context-target": "Reported",
    "context-reported": "Also reported",
    "context-deleted": "Deleted",
    "reaction-by": "Reacted with {{emoji}} by {{names}}",
    "context-more": "Further messages are not shown.",
    "context-none":
      "This report has no conversation to show — it does not target a message, or the message is gone from the database.",
    "context-failed": "The conversation could not be loaded.",
    "context-error": "Could not load the conversation",

    sanction: "Sanction",
    "sanction-note":
      "Irreversible, and independent of the verdict: the report stays in its queue.",
    "delete-message": "Delete the message",
    "delete-event": "Delete the event",
    "delete-account": "Delete the account",
    "delete-post": "Delete the post",
    "delete-comment": "Delete the comment",
    "sanction-none":
      "The reported item no longer has an identifier — there is nothing left to delete.",
    "sanction-deleted": "Deleted",
    "sanction-done": "Deleted",
    "sanction-error": "Could not delete",
    sanctioning: "Deleting…",
    "confirm-title": "Delete for good?",
    "confirm-message":
      "Message {{id}} will be removed from its conversation. This cannot be undone.",
    "confirm-event":
      "Event {{id}} will be removed, along with its applications. This cannot be undone.",
    "confirm-account":
      "The account {{name}} will be removed, along with everything attached to it. This cannot be undone.",
    "confirm-post":
      "Post {{id}} will be erased for good, along with its photos, hearts and comments. This cannot be undone.",
    "confirm-comment":
      "Comment {{id}} will be erased for good, along with its replies. This cannot be undone.",
    cancel: "Cancel",
    confirm: "Delete",
  },

  /**
   * La file de validation du fil d'actualité.
   *
   * ⚠️ **Les libellés de statut ne recopient pas les codes.** `scanning` dit
   * « la machine regarde », `pending_review` « c'est ton tour » : écrits tels
   * quels, les deux se confondraient en une seule file d'attente, alors qu'un
   * seul des deux se relève à la main.
   *
   * ⚠️ **« Not judged » n'est pas « nothing to report ».** Les deux se disent
   * donc autrement, et le premier porte un ton d'alerte : classifieur
   * injoignable ou clé d'API absente, la publication attend parce qu'on n'a
   * *pas pu* la regarder.
   */
  posts: {
    "status-pending-review": "Waiting for review",
    "status-scanning": "Being scanned",
    "status-approved": "Published",
    "status-rejected": "Taken down",

    "filter-all": "All",
    "deleted-exclude": "Still up",
    "deleted-only": "Removed by author",
    "filter-type-label": "Kind",
    "filter-deleted-label": "Author removal",
    "filter-sort-label": "Sort",
    "sort-date": "Date",
    "sort-likes": "Hearts",
    "sort-comments": "Comments",
    "order-asc": "Oldest first",
    "order-desc": "Newest first",
    "search-placeholder": "Search a text or an author",

    "scanning-warning":
      "Scanning takes a few seconds. A post that stays here is stuck: the background job died without writing its verdict, and nothing retries it. Judge it by hand.",

    total: "{{count}} posts",

    "empty-queue-title": "Queue is empty",
    "empty-queue-description":
      "Nothing is waiting for a decision. Posts land here when the classifier is unsure, or could not be reached.",
    "empty-title": "Nothing here",
    "empty-description": "No post matches these filters right now.",
    "empty-feed-description": "The feed is empty: nobody has published yet.",

    "type-gallery": "Gallery",
    "type-thread": "Battle report",

    "score-unjudged": "Not judged",
    "score-clean": "Clean · {{score}}",
    "score-flagged": "Flagged · {{score}}",
    "score-flagged-category": "{{category}} · {{score}}",

    "scores-show": "Show scores",
    "scores-model": "{{model}}, threshold {{threshold}}",
    "scores-photo": "Photo {{position}}",

    approve: "Publish",
    approving: "Publishing…",
    reject: "Take down",
    rejecting: "Taking down…",
    delete: "Erase",
    deleting: "Erasing…",

    "confirm-approve-title": "Publish this post?",
    "confirm-approve-body":
      "The post by {{author}} becomes visible to everyone, and its author is notified. You can take it down again afterwards.",
    "confirm-reject-title": "Take this post down?",
    "confirm-reject-body":
      "The post by {{author}} becomes invisible to everyone but its author, who is notified. Nothing is erased, and you can publish it again afterwards.",
    "confirm-delete-title": "Erase for good?",
    "confirm-delete-body":
      "The post by {{author}} will be erased, along with its photos, hearts and comments. This cannot be undone.",

    "toast-approved": "Post published.",
    "toast-rejected": "Post taken down.",
    "toast-deleted": "Post erased.",

    "list-error": "Could not load the feed.",
    "action-error": "The action failed.",
    "error-title": "The feed could not be loaded",
    "error-description": "The server did not answer. Try again in a moment.",

    previous: "Previous",
    next: "Next",
    "page-of": "Page {{page}} of {{pages}}",

    "post-title": "Post #{{id}}",
    "deleted-by-author": "Removed by its author",
    engagement: "{{likes}} hearts · {{comments}} comments",

    "detail-error": "Could not load the post.",
    "missing-title": "This post no longer exists",
    "missing-description":
      "It has been erased, or this address never pointed at anything.",
    "back-to-list": "Back to the feed",

    "published-by": "Published by",
    "no-content": "No text: the photos are the post.",

    "counter-likes": "Hearts",
    "counter-comments": "Comments",
    "counter-reports": "Reports",
    "counter-photos": "Photos",

    "moderated-on": "Decided on {{date}}",
    "not-moderated-yet": "No administrator has ruled on this post yet.",

    comments: "Comments",
    "comments-empty": "Nobody has commented.",
    "comments-total": "{{count}} in total",
    "comments-error": "Could not load the comments.",
    "comment-deleted": "Deleted by its author",
    "likes-count": "{{count}} hearts",
    "reports-count": "{{count}} reports",

    "delete-comment": "Erase comment",
    "confirm-delete-comment-title": "Erase this comment?",
    "confirm-delete-comment-body":
      "The comment by {{author}} will be erased, along with its replies. This cannot be undone, and its author is not notified.",
    "toast-comment-deleted": "Comment erased.",

    likes: "Hearts",
    "likes-empty": "Nobody has hearted this post.",
    "likes-show": "Show the {{count}} accounts",
    "likes-total": "{{count}} in total",
    "likes-error": "Could not load the hearts.",
  },

  /**
   * Les gestes de modération — comptes et événements.
   *
   * ⚠️ Un scope à part **parce que deux écrans s'en servent** : la fiche d'un
   * compte et celle d'un événement partagent la moitié de ces phrases (motif,
   * annuler, « décidé par »). Laissées dans `accounts`, les événements auraient
   * dû aller y piocher — ou en recopier une seconde version, qui aurait dérivé.
   */
  moderation: {
    actions: "Actions",
    cancel: "Cancel",
    reason: "Reason (internal)",
    "reason-hint":
      "Written for the next administrator, never shown to the person concerned.",
    "no-reason": "No reason was recorded.",
    "decided-by": "Decided by",

    "state-active": "Active",
    "state-disabled": "Disabled",
    "active-note":
      "This account can sign in normally. Disabling it closes the door without deleting anything — the move can be undone.",
    "disabled-since": "Disabled on {{date}}.",

    disable: "Disable",
    disabling: "Disabling…",
    "disable-title": "Disable this account?",
    "disable-body":
      "{{name}} will no longer be able to sign in, and every open session will be cut. Nothing is deleted, and you can undo this.",
    "disable-confirm": "Disable",
    "disabled-done": "Account disabled",
    "sessions-revoked": "{{count}} sessions were cut.",
    "disable-error": "Could not change the account state",

    enable: "Re-enable",
    enabling: "Re-enabling…",
    "enable-title": "Re-enable this account?",
    "enable-body":
      "{{name}} will be able to sign in again. Signing back in is up to them — no session is restored.",
    "enable-confirm": "Re-enable",
    "enabled-done": "Account re-enabled",

    "delete-account": "Delete the account",
    deleting: "Deleting…",
    "delete-account-title": "Delete this account?",
    "delete-account-body":
      "{{name}}, their profile, their armies and the events they organise — with their threads — go for good. This cannot be undone. To close the account without destroying anything, disable it instead.",
    "delete-account-confirm": "Delete for good",
    "account-deleted": "Account deleted",
    "account-delete-error": "Could not delete the account",

    flags: "Flags",
    "flags-empty": "No flag on this account.",
    "flags-note":
      "Internal notes, invisible to the person concerned: a flag grants and removes no right. It tells the next administrator what the previous one noticed.",
    "flag-set": "Flag saved",
    "flag-removed": "Flag removed",
    "flag-error": "Could not change the flags",
    "flag-note-label": "Note for “{{flag}}”",
    "flag-save": "Save",
    "flag-edit": "Edit",
    "no-note": "No note.",

    "flag-watch": "To watch",
    "flag-problematic": "Problematic",
    "flag-spam": "Spam",
    "flag-harassment": "Harassment",
    "flag-scam": "Scam",
    "flag-underage": "Underage",
    "flag-ban-evasion": "Ban evasion",
    "flag-trusted": "Trusted",

    "event-state-visible": "Visible",
    "event-state-hidden": "Taken down",
    "event-visible-note":
      "This event shows up in searches and on its own page. Taking it down hides it from everyone but administrators, without deleting anything.",
    "unapproved-since": "Taken down on {{date}}.",

    "close-event": "Close",
    closing: "Closing…",
    "close-event-title": "Close this event?",
    "close-event-body":
      "“{{name}}” gets the final status: the game is deemed played. It stays visible to everyone — closing does not hide.",
    "close-event-confirm": "Close",
    "event-closed": "Event closed",
    "event-close-error": "Could not close the event",

    unapprove: "Take down",
    unapproving: "Taking down…",
    "unapprove-title": "Take this event down?",
    "unapprove-body":
      "“{{name}}” will vanish from searches, from its own page and from its organiser's list — nobody but administrators will see it. Applications will be turned away. Nothing is deleted, and you can undo this.",
    "unapprove-confirm": "Take down",
    "event-unapproved": "Event taken down",
    "event-approval-error": "Could not change the event visibility",

    approve: "Put back",
    approving: "Putting back…",
    "approve-title": "Put this event back?",
    "approve-body":
      "“{{name}}” becomes visible again to everyone, and applications reopen.",
    "approve-confirm": "Put back",
    "event-approved": "Event put back",

    "delete-event": "Delete the event",
    "delete-event-title": "Delete this event?",
    "delete-event-body":
      "“{{name}}”, its sign-ups and its whole discussion thread go for good. This cannot be undone. To hide it without destroying anything, take it down instead.",
    "delete-event-confirm": "Delete for good",
    "event-deleted": "Event deleted",
    "event-delete-error": "Could not delete the event",
  },

  /**
   * La lecture des discussions par l'administration.
   *
   * ⚠️ Scope partagé lui aussi : la visionneuse sert la fiche d'un compte et
   * celle d'un événement.
   */
  conversations: {
    title: "Conversation",
    "title-plural": "Conversations",
    "event-title": "Event thread",
    open: "Read",
    close: "Close the conversation",
    "empty-list": "This account takes part in no conversation.",
    "list-error": "Could not load the conversations",
    "load-error": "Could not load the conversation",
    failed: "The conversation could not be loaded.",
    gone: "This conversation is gone — deleted along with its event, most likely.",
    empty: "Nothing was written here.",
    "kind-direct": "Direct",
    "kind-group": "Group",
    "event-thread": "Thread of “{{name}}”",
    "message-count": "{{count}} messages",
    "more-members": "and {{count}} more",
    silent: "nothing written",
    left: "left",
    system: "System",
    reported: "Reported",
    deleted: "Deleted",
    beginning: "Beginning of the conversation.",
    "load-older": "Load the older messages",
    "counter-conversations": "Conversations",
    "counter-messages": "Messages sent",
    "reaction-by": "{{emoji}} by {{names}}",
  },

  notFound: {
    title: "Page not found",
    description: "This address matches no screen of the administration.",
    back: "Back to the dashboard",
  },

  navigation: {
    "group-overview": "Overview",
    "group-moderation": "Moderation",
    "group-community": "Community",
    "group-system": "System",
    dashboard: "Dashboard",
    reports: "Reports",
    posts: "Feed",
    events: "Events",
    accounts: "Accounts",
    settings: "Settings",
  },

  placeholder: {
    title: "Not built yet",
    description:
      "This screen has its route and its place in the navigation. Its content is the next step.",
  },

  errors: {
    timeout: "The server did not answer in time. Try again in a moment.",
    unauthorized: "Session expired, or access denied.",
    forbidden: "You are not allowed to do this.",
    server: "The server returned an error.",
    refused: "Request refused by the server.",
    unexpected: "Unexpected error.",
  },

  locales: {
    en: "English",
    fr: "Français",
  },

  a11y: {
    "to-light-theme": "Switch to light theme",
    "to-dark-theme": "Switch to dark theme",
    "change-language": "Change language",
    "show-password": "Show password",
    "hide-password": "Hide password",
    "main-navigation": "Main navigation",
    breadcrumb: "Breadcrumb",
    home: "Back to the dashboard",
    "open-navigation": "Open navigation",
    "close-navigation": "Close navigation",
    "collapse-sidebar": "Collapse the sidebar",
    "expand-sidebar": "Expand the sidebar",
    "filter-reports": "Filter reports by status",
    "search-accounts": "Search accounts by email or username",
    "sort-accounts": "Sort accounts",
    "search-events": "Search events by name or organiser",
    "sort-events": "Sort events",
    "filter-events": "Filter events by status",
    "filter-posts": "Filter posts by status",
    "search-posts": "Search posts by text or author",
    "filter-post-types": "Filter posts by kind",
    "filter-deleted-posts": "Filter posts removed by their author",
    "sort-posts": "Sort posts",
    "post-photo": "Photo {{position}} of the post",
    "reported-photo": "Photo {{position}} of the reported post",
    "clear-search": "Clear the search",
    "chart-granularity": "Change the chart period",
  },
};

/**
 * Forme d'un dictionnaire — celle de `en`.
 *
 * Sert à deux choses : contraindre `fr` (cf. l'avertissement ci-dessus) et
 * typer les clés de `useTranslations` scope par scope.
 */
export type Translations = typeof en;

/** Les scopes disponibles, dérivés du dictionnaire de référence. */
export type Scope = keyof Translations;

export const fr: Translations = {
  global: {
    "app-name": "Wargame Admin",
    "sign-out": "Se déconnecter",
    "account-deleted": "Compte supprimé",
  },

  signIn: {
    title: "Connexion",
    subtitle: "Réservé aux comptes portant le rôle administrateur.",
    email: "E-mail",
    password: "Mot de passe",
    submit: "Se connecter",
    submitting: "Connexion…",
    "in-progress": "Connexion en cours",
    "failed-title": "Connexion impossible",
    "denied-title": "Accès refusé",
    "denied-description": "Ce compte n'a pas le rôle administrateur.",
    tagline: "Le poste de commandement de WargameApp.",
    "feature-reports": "Traiter la file des signalements",
    "feature-events": "Consulter, éditer et annuler les événements",
    "feature-accounts": "Créer des comptes et gérer les rôles",
    footnote:
      "Console interne — aucune donnée n'est stockée ici, tout vient de l'API WargameApp.",
  },

  dashboard: {
    "signed-in-as": "Connecté en tant que",
    "session-error": "Session illisible",
    "load-error": "Tableau de bord illisible",
    "panel-error": "Lecture impossible.",

    // Poste de garde.
    "watch-eyebrow": "Ce qui demande une décision",
    "pending-label": "signalements en attente",
    "open-queue": "Ouvrir la file",
    "queue-clear": "File à jour",
    "queue-clear-hint": "Rien n'attend de décision.",
    "queue-unreadable": "La file n'a pas pu être lue.",
    "oldest-since": "Le plus ancien attend depuis {{duration}}",
    "sla-label": "Temps écoulé sur le délai de {{hours}} heures",
    "sla-target": "Objectif {{hours}} h",
    "sla-fresh": "Dans les délais",
    "sla-soon": "Échéance proche",
    "sla-late": "Hors délai",

    // Les quatre chiffres de la plateforme.
    "pulse-eyebrow": "Sur la plateforme",
    "tile-accounts": "Comptes",
    "tile-events": "Événements",
    "tile-upcoming": "Parties à venir",
    "tile-feed": "Publications",
    "tile-handled": "Signalements traités",
    "hint-last-signup": "Dernière inscription {{when}}",
    "hint-open-events": "{{count}} acceptent encore des joueurs",
    "hint-next-game": "La prochaine {{when}}",
    "hint-feed-pending": "{{count}} à relever",
    "hint-dismissed": "{{count}} classés sans suite",

    // Les trois panneaux.
    "see-all": "Tout voir",
    "see-all-queue": "Tout voir — file de modération",
    "see-all-events": "Tout voir — événements",
    "see-all-accounts": "Tout voir — comptes",
    "see-all-feed": "Tout voir — file du fil",
    "queue-title": "Les plus anciens d'abord",
    "queue-empty": "Aucun signalement n'attend.",
    "reported-by": "Signalé par",
    "feed-title": "Publications à relever",
    "feed-empty": "Aucune publication n'attend.",
    "upcoming-title": "Prochaines parties",
    "upcoming-empty": "Aucune partie n'est programmée.",
    "accounts-title": "Nouveaux comptes",
    "accounts-empty": "Aucun compte pour l'instant.",

    // Tendances — les six graphiques.
    "stats-eyebrow": "Tendances",
    "granularity-day": "Par jour",
    "granularity-month": "Par mois",
    "granularity-year": "Par année",
    "chart-empty": "Rien d'enregistré sur cette période.",
    "chart-accounts-title": "Inscriptions",
    "chart-accounts-caption": "Comptes créés par période.",
    "chart-events-title": "Événements créés",
    "chart-events-caption": "Parties ouvertes par période.",
    "chart-reports-title": "Signalements déposés",
    "chart-reports-caption":
      "La charge entrante, quel que soit le sort réservé depuis.",
    "chart-resolution-title": "Délai de décision",
    "chart-resolution-caption":
      "Attente médiane avant verdict, contre l'objectif de 24 heures.",
    "chart-reasons-title": "Signalements par motif",
    "chart-reasons-caption":
      "Où porte l'effort de modération, sur toute la fenêtre.",
    "chart-fill-title": "Places pourvues",
    "chart-fill-caption": "Part des places offertes qui trouvent preneur.",
  },

  accounts: {
    "search-placeholder": "Rechercher par e-mail ou pseudo",
    "search-error": "Recherche impossible",
    "list-error": "Liste des comptes illisible",
    "follows-error": "Abonnements illisibles",
    "detail-error": "Compte illisible",
    total: "{{count}} comptes",
    "page-of": "Page {{page}} sur {{pages}}",
    previous: "Précédent",
    next: "Suivant",
    "sort-created": "Plus récents",
    "sort-username": "Pseudo",
    "order-asc": "Croissant",
    "order-desc": "Décroissant",
    unverified: "Non vérifié",

    "empty-title": "Aucun compte",
    "empty-filtered": "Aucun compte ne correspond à ces filtres.",
    "empty-none": "La base ne contient encore aucun compte.",
    "error-title": "La liste n'a pas pu être chargée",
    "error-description":
      "Le serveur n'a pas répondu. Réessayez dans un instant.",

    "missing-title": "Compte introuvable",
    "missing-description":
      "Cette adresse ne correspond à aucun compte — il a pu être supprimé depuis.",
    "back-to-list": "Revenir à la liste",
    registered: "Inscrit le {{date}}",
    profile: "Profil public",
    "profile-none": "Ce compte n'a pas de profil public.",
    "no-bio": "Pas de biographie.",

    armies: "Armées",
    "armies-empty": "Aucune armée déclarée.",
    "events-created": "Événements créés",
    "events-created-empty": "N'a rien organisé.",
    "events-joined": "Événements rejoints",
    "events-joined-empty": "N'a rejoint aucun événement d'un tiers.",
    "events-error": "Événements de ce compte illisibles",
    "events-truncated": "et {{count}} autres — cherchez-les depuis Événements.",
    "event-no-date": "sans date",
    "reports-filed": "Signalements émis",
    "reports-received": "Signalements reçus",

    followed: "Abonnements",
    "followed-empty": "Ne suit personne.",
    followers: "Abonnés",
    "followers-empty": "Personne ne suit ce compte.",
  },

  geo: {
    open: "Chercher autour d'un lieu",
    active: "Autour de {{place}} · {{radius}} km",
    point: "un point",
    note: "Les adresses viennent de Photon, un moteur de recherche OpenStreetMap — seules des coordonnées parviennent au serveur.",
    address: "Lieu",
    "address-placeholder": "Lyon, 12 rue…, 69003",
    "address-empty": "Aucun lieu ne correspond.",
    "address-count": "{{count}} lieux proposés",
    radius: "Rayon (km)",
    clear: "Effacer",
    distance: "{{km}} km",
  },

  events: {
    "search-placeholder": "Rechercher par nom ou organisateur",
    "list-error": "Liste des événements illisible",
    "detail-error": "Événement illisible",
    total: "{{count}} événements",
    "page-of": "Page {{page}} sur {{pages}}",
    previous: "Précédent",
    next: "Suivant",

    "filter-all": "Tous",
    "filter-hint": "Filtres, tri et pagination sont appliqués par le serveur.",
    "status-open": "Ouvert",
    "status-complete": "Complet",
    "status-close": "Terminé",

    "sort-date": "Date de partie",
    "sort-created": "Plus récents",
    "sort-name": "Nom",
    "sort-distance": "Distance",
    "sort-distance-hint": "Choisissez un lieu pour trier par distance.",
    "order-asc": "Croissant",
    "order-desc": "Décroissant",

    "no-date": "Date non fixée",
    "no-location": "Sans lieu",
    "places-filled": "{{filled}} / {{places}} places",
    "pending-count": "{{count}} en attente",

    "empty-title": "Aucun événement",
    "empty-filtered": "Aucun événement ne correspond à ces filtres.",
    "empty-none": "Aucun événement n'est listé.",
    "error-title": "La liste n'a pas pu être chargée",
    "error-description":
      "Le serveur n'a pas répondu. Réessayez dans un instant.",

    "missing-title": "Événement introuvable",
    "missing-description":
      "Cette adresse ne correspond à aucun événement — il a pu être supprimé depuis.",
    "back-to-list": "Revenir à la liste",

    "organised-by": "Organisé par",
    "created-on": "créé le {{date}}",
    "no-description": "Pas de description.",
    points: "{{count}} points",
    "subtype-solo": "Solo",
    "subtype-duo": "Duo",
    "subtype-team": "Équipe",
    "level-beginner": "Débutant",
    "level-intermediate": "Intermédiaire",
    "level-advanced": "Confirmé",
    "level-pro": "Expert",
    "finality-equality": "Égalité",
    "finality-abandoned": "Abandon",

    "counter-places": "Places",
    "counter-participants": "Confirmés",
    "counter-pending": "Postulants",
    "counter-winners": "Vainqueurs",
    "counter-total": "Personnes engagées",

    location: "Lieu",
    "location-unknown": "Aucun lieu enregistré pour cet événement.",
    "location-public": "Adresse publique",
    "location-hidden": "Adresse masquée",
    "location-hidden-note":
      "L'organisateur a choisi de ne pas publier cette adresse. Elle s'affiche ici parce que les routes d'administration la rendent brute — elle reste masquée partout ailleurs.",
    coordinates: "{{lat}}, {{lng}}",
    "distance-from-centre": "à {{km}} km du lieu cherché",

    participants: "Joueurs confirmés",
    "participants-empty": "Personne n'a encore été validé.",
    applicants: "Postulants",
    "applicants-empty": "Aucune candidature en attente.",
    "no-army": "sans armée",
    winner: "Vainqueur",
    ghosted: "Absent",

    conversation: "Conversation de groupe",
    "conversation-none": "Cet événement n'a pas de fil de discussion.",
    "conversation-exists": "Le fil nº {{id}} est rattaché à cet événement.",
  },

  reports: {
    "open-post": "Ouvrir la publication",
    "status-pending": "En attente",
    "status-actioned": "Retenus",
    "status-dismissed": "Classés sans suite",
    "help-open": "Comment fonctionnent verdict et sanction",
    "target-message": "Message",
    "target-account": "Compte",
    "target-account-label": "Compte visé",
    "target-event": "Événement",
    "target-post": "Publication",
    "target-comment": "Commentaire",
    "reason-spam": "Spam",
    "reason-harassment": "Harcèlement",
    "reason-inappropriate-content": "Contenu inapproprié",
    "reason-impersonation": "Usurpation d'identité",
    "reason-child-safety": "Sécurité d'un mineur",
    "reason-other": "Autre",
    "reported-by": "Signalé par",
    "no-content": "Le contenu signalé n'a pas été conservé.",
    "empty-title": "Rien à examiner",
    "empty-pending":
      "La file est vide. Les signalements arrivent ici dès qu'un joueur en dépose un.",
    "empty-closed": "Aucun signalement n'a ce statut pour l'instant.",
    "error-title": "La file n'a pas pu être chargée",
    "error-description":
      "Le serveur n'a pas renvoyé la file de modération. Réessaie dans un instant.",
    retry: "Réessayer",
    "load-error": "Impossible de charger la file de modération",

    verdict: "Verdict",
    "verdict-note":
      "Un verdict ne fait que classer le signalement : rien n'est supprimé, et personne n'est prévenu — ni le signaleur, ni la personne visée.",
    dismiss: "Classer sans suite",
    uphold: "Retenir",
    "requalify-to-actioned": "Retenir finalement",
    "requalify-to-dismissed": "Classer sans suite finalement",
    "reviewed-by": "Examiné par",
    "no-reopen":
      "Un signalement clos ne revient pas dans la file en attente — seulement à l'autre verdict.",
    resolving: "Enregistrement…",
    resolved: "Verdict enregistré",
    "resolve-error": "Impossible d'enregistrer le verdict",

    "context-open": "Voir la conversation",
    "context-title": "Autour du message signalé",
    "context-close": "Fermer la conversation",
    "context-direct": "Conversation directe.",
    "context-group": "Conversation de groupe.",
    "context-target": "Signalé",
    "context-reported": "Signalé aussi",
    "context-deleted": "Supprimé",
    "reaction-by": "Réaction {{emoji}} de {{names}}",
    "context-more": "Les messages au-delà ne sont pas affichés.",
    "context-none":
      "Ce signalement n'a pas de conversation à montrer — il ne vise pas un message, ou le message a disparu de la base.",
    "context-failed": "La conversation n'a pas pu être chargée.",
    "context-error": "Impossible de charger la conversation",

    sanction: "Sanction",
    "sanction-note":
      "Irréversible, et indépendante du verdict : le signalement reste dans sa file.",
    "delete-message": "Supprimer le message",
    "delete-event": "Supprimer l'événement",
    "delete-account": "Supprimer le compte",
    "delete-post": "Supprimer la publication",
    "delete-comment": "Supprimer le commentaire",
    "sanction-none":
      "Le contenu visé n'a plus d'identifiant — il n'y a plus rien à supprimer.",
    "sanction-deleted": "Supprimé",
    "sanction-done": "Supprimé",
    "sanction-error": "Suppression impossible",
    sanctioning: "Suppression…",
    "confirm-title": "Supprimer définitivement ?",
    "confirm-message":
      "Le message {{id}} sera retiré de sa conversation. C'est sans retour.",
    "confirm-event":
      "L'événement {{id}} sera retiré, avec les candidatures qui s'y rattachent. C'est sans retour.",
    "confirm-account":
      "Le compte {{name}} sera retiré, avec tout ce qui s'y rattache. C'est sans retour.",
    "confirm-post":
      "La publication {{id}} sera effacée définitivement, avec ses photos, ses cœurs et ses commentaires. C'est sans retour.",
    "confirm-comment":
      "Le commentaire {{id}} sera effacé définitivement, avec ses réponses. C'est sans retour.",
    cancel: "Annuler",
    confirm: "Supprimer",
  },

  posts: {
    "status-pending-review": "À relever",
    "status-scanning": "Analyse en cours",
    "status-approved": "En ligne",
    "status-rejected": "Retirées",

    "filter-all": "Toutes",
    "deleted-exclude": "Encore en ligne",
    "deleted-only": "Retirées par l'auteur",
    "filter-type-label": "Nature",
    "filter-deleted-label": "Retrait par l'auteur",
    "filter-sort-label": "Tri",
    "sort-date": "Date",
    "sort-likes": "Cœurs",
    "sort-comments": "Commentaires",
    "order-asc": "Les plus anciennes d'abord",
    "order-desc": "Les plus récentes d'abord",
    "search-placeholder": "Chercher un texte ou un auteur",

    "scanning-warning":
      "L'analyse prend quelques secondes. Une publication qui reste ici est bloquée : la tâche de fond est morte sans écrire son verdict, et aucune reprise n'est prévue. Tranche à la main.",

    total: "{{count}} publications",

    "empty-queue-title": "File vide",
    "empty-queue-description":
      "Rien n'attend de décision. Une publication arrive ici quand le classifieur a un doute, ou qu'il était injoignable.",
    "empty-title": "Rien ici",
    "empty-description":
      "Aucune publication ne correspond à ces filtres pour le moment.",
    "empty-feed-description": "Le fil est vide : personne n'a encore publié.",

    "type-gallery": "Galerie",
    "type-thread": "Compte rendu de partie",

    "score-unjudged": "Non jugée",
    "score-clean": "Rien à signaler · {{score}}",
    "score-flagged": "Signalée · {{score}}",
    "score-flagged-category": "{{category}} · {{score}}",

    "scores-show": "Voir les scores",
    "scores-model": "{{model}}, seuil {{threshold}}",
    "scores-photo": "Photo {{position}}",

    approve: "Publier",
    approving: "Publication…",
    reject: "Retirer",
    rejecting: "Retrait…",
    delete: "Effacer",
    deleting: "Effacement…",

    "confirm-approve-title": "Publier cette publication ?",
    "confirm-approve-body":
      "La publication de {{author}} devient visible de tous, et son auteur en est averti. Tu pourras la retirer ensuite.",
    "confirm-reject-title": "Retirer cette publication ?",
    "confirm-reject-body":
      "La publication de {{author}} devient invisible de tous sauf de son auteur, qui en est averti. Rien n'est effacé, et tu pourras la publier ensuite.",
    "confirm-delete-title": "Effacer définitivement ?",
    "confirm-delete-body":
      "La publication de {{author}} sera effacée, avec ses photos, ses cœurs et ses commentaires. C'est sans retour.",

    "toast-approved": "Publication publiée.",
    "toast-rejected": "Publication retirée.",
    "toast-deleted": "Publication effacée.",

    "list-error": "Chargement du fil impossible.",
    "action-error": "L'action a échoué.",
    "error-title": "Le fil n'a pas pu être chargé",
    "error-description":
      "Le serveur n'a pas répondu. Réessaie dans un instant.",

    previous: "Précédent",
    next: "Suivant",
    "page-of": "Page {{page}} sur {{pages}}",

    "post-title": "Publication n° {{id}}",
    "deleted-by-author": "Retirée par son auteur",
    engagement: "{{likes}} cœurs · {{comments}} commentaires",

    "detail-error": "Chargement de la publication impossible.",
    "missing-title": "Cette publication n'existe plus",
    "missing-description":
      "Elle a été effacée, ou cette adresse n'a jamais désigné quoi que ce soit.",
    "back-to-list": "Revenir au fil",

    "published-by": "Publiée par",
    "no-content": "Aucun texte : ce sont les photos qui font la publication.",

    "counter-likes": "Cœurs",
    "counter-comments": "Commentaires",
    "counter-reports": "Signalements",
    "counter-photos": "Photos",

    "moderated-on": "Tranchée le {{date}}",
    "not-moderated-yet":
      "Aucun administrateur ne s'est encore prononcé sur cette publication.",

    comments: "Commentaires",
    "comments-empty": "Personne n'a commenté.",
    "comments-total": "{{count}} au total",
    "comments-error": "Chargement des commentaires impossible.",
    "comment-deleted": "Supprimé par son auteur",
    "likes-count": "{{count}} cœurs",
    "reports-count": "{{count}} signalements",

    "delete-comment": "Effacer le commentaire",
    "confirm-delete-comment-title": "Effacer ce commentaire ?",
    "confirm-delete-comment-body":
      "Le commentaire de {{author}} sera effacé, avec ses réponses. C'est sans retour, et son auteur n'en est pas averti.",
    "toast-comment-deleted": "Commentaire effacé.",

    likes: "Cœurs",
    "likes-empty": "Personne n'a aimé cette publication.",
    "likes-show": "Voir les {{count}} comptes",
    "likes-total": "{{count}} au total",
    "likes-error": "Chargement des cœurs impossible.",
  },

  moderation: {
    actions: "Actions",
    cancel: "Annuler",
    reason: "Motif (interne)",
    "reason-hint":
      "Écrit pour l'administrateur suivant, jamais montré à l'intéressé.",
    "no-reason": "Aucun motif n'a été consigné.",
    "decided-by": "Décidé par",

    "state-active": "Actif",
    "state-disabled": "Désactivé",
    "active-note":
      "Ce compte se connecte normalement. Le désactiver ferme la porte sans rien supprimer — le geste se défait.",
    "disabled-since": "Désactivé le {{date}}.",

    disable: "Désactiver",
    disabling: "Désactivation…",
    "disable-title": "Désactiver ce compte ?",
    "disable-body":
      "{{name}} ne pourra plus se connecter, et ses sessions ouvertes seront coupées. Rien n'est supprimé, et vous pourrez revenir en arrière.",
    "disable-confirm": "Désactiver",
    "disabled-done": "Compte désactivé",
    "sessions-revoked": "{{count}} sessions ont été coupées.",
    "disable-error": "Impossible de changer l'état du compte",

    enable: "Réactiver",
    enabling: "Réactivation…",
    "enable-title": "Réactiver ce compte ?",
    "enable-body":
      "{{name}} pourra de nouveau se connecter. À lui de rouvrir une session : aucune n'est rétablie.",
    "enable-confirm": "Réactiver",
    "enabled-done": "Compte réactivé",

    "delete-account": "Supprimer le compte",
    deleting: "Suppression…",
    "delete-account-title": "Supprimer ce compte ?",
    "delete-account-body":
      "{{name}}, son profil, ses armées et les événements qu'il organise — avec leurs fils — partent définitivement. C'est sans retour. Pour fermer le compte sans rien détruire, désactivez-le.",
    "delete-account-confirm": "Supprimer définitivement",
    "account-deleted": "Compte supprimé",
    "account-delete-error": "Impossible de supprimer le compte",

    flags: "Drapeaux",
    "flags-empty": "Aucun drapeau sur ce compte.",
    "flags-note":
      "Notes internes, invisibles de l'intéressé : un drapeau n'ouvre ni ne ferme aucun droit. Il dit à l'administrateur suivant ce que le précédent avait remarqué.",
    "flag-set": "Drapeau enregistré",
    "flag-removed": "Drapeau retiré",
    "flag-error": "Impossible de modifier les drapeaux",
    "flag-note-label": "Note pour « {{flag}} »",
    "flag-save": "Enregistrer",
    "flag-edit": "Modifier",
    "no-note": "Aucune note.",

    "flag-watch": "À surveiller",
    "flag-problematic": "Problématique",
    "flag-spam": "Spam",
    "flag-harassment": "Harcèlement",
    "flag-scam": "Arnaque",
    "flag-underage": "Mineur présumé",
    "flag-ban-evasion": "Contournement de sanction",
    "flag-trusted": "De confiance",

    "event-state-visible": "Visible",
    "event-state-hidden": "Retiré",
    "event-visible-note":
      "Cet événement apparaît dans les recherches et sur sa propre fiche. Le retirer le cache à tout le monde sauf aux administrateurs, sans rien supprimer.",
    "unapproved-since": "Retiré le {{date}}.",

    "close-event": "Fermer",
    closing: "Fermeture…",
    "close-event-title": "Fermer cet événement ?",
    "close-event-body":
      "« {{name}} » prend le statut final : la partie est réputée jouée. Il reste visible de tous — fermer ne cache pas.",
    "close-event-confirm": "Fermer",
    "event-closed": "Événement fermé",
    "event-close-error": "Impossible de fermer l'événement",

    unapprove: "Retirer",
    unapproving: "Retrait…",
    "unapprove-title": "Retirer cet événement ?",
    "unapprove-body":
      "« {{name}} » disparaîtra des recherches, de sa fiche et de la liste de son organisateur — personne d'autre que les administrateurs ne le verra. Les candidatures seront refusées. Rien n'est supprimé, et vous pourrez revenir en arrière.",
    "unapprove-confirm": "Retirer",
    "event-unapproved": "Événement retiré",
    "event-approval-error":
      "Impossible de changer la visibilité de l'événement",

    approve: "Remettre",
    approving: "Remise…",
    "approve-title": "Remettre cet événement ?",
    "approve-body":
      "« {{name}} » redevient visible de tous, et les candidatures rouvrent.",
    "approve-confirm": "Remettre",
    "event-approved": "Événement remis",

    "delete-event": "Supprimer l'événement",
    "delete-event-title": "Supprimer cet événement ?",
    "delete-event-body":
      "« {{name}} », ses inscriptions et tout son fil de discussion partent définitivement. C'est sans retour. Pour le cacher sans rien détruire, retirez-le.",
    "delete-event-confirm": "Supprimer définitivement",
    "event-deleted": "Événement supprimé",
    "event-delete-error": "Impossible de supprimer l'événement",
  },

  conversations: {
    title: "Discussion",
    "title-plural": "Discussions",
    "event-title": "Fil de l'événement",
    open: "Lire",
    close: "Fermer la discussion",
    "empty-list": "Ce compte ne participe à aucune discussion.",
    "list-error": "Impossible de charger les discussions",
    "load-error": "Impossible de charger la discussion",
    failed: "La discussion n'a pas pu être chargée.",
    gone: "Cette discussion a disparu — supprimée avec son événement, le plus souvent.",
    empty: "Rien n'y a été écrit.",
    "kind-direct": "Directe",
    "kind-group": "Groupe",
    "event-thread": "Fil de « {{name}} »",
    "message-count": "{{count}} messages",
    "more-members": "et {{count}} de plus",
    silent: "rien d'écrit",
    left: "partie",
    system: "Système",
    reported: "Signalé",
    deleted: "Supprimé",
    beginning: "Début de la discussion.",
    "load-older": "Charger les messages plus anciens",
    "counter-conversations": "Discussions",
    "counter-messages": "Messages écrits",
    "reaction-by": "{{emoji}} par {{names}}",
  },

  notFound: {
    title: "Page introuvable",
    description:
      "Cette adresse ne correspond à aucun écran de l'administration.",
    back: "Retour au tableau de bord",
  },

  navigation: {
    "group-overview": "Vue d'ensemble",
    "group-moderation": "Modération",
    "group-community": "Communauté",
    "group-system": "Système",
    dashboard: "Tableau de bord",
    reports: "Signalements",
    posts: "Fil",
    events: "Événements",
    accounts: "Comptes",
    settings: "Paramètres",
  },

  placeholder: {
    title: "Pas encore construit",
    description:
      "Cet écran a sa route et sa place dans la navigation. Son contenu est l'étape suivante.",
  },

  errors: {
    timeout: "Le serveur n'a pas répondu à temps. Réessaie dans un instant.",
    unauthorized: "Session expirée ou accès refusé.",
    forbidden: "Tu n'as pas les droits pour cette action.",
    server: "Le serveur a renvoyé une erreur.",
    refused: "Requête refusée par le serveur.",
    unexpected: "Erreur inattendue.",
  },

  locales: {
    en: "English",
    fr: "Français",
  },

  a11y: {
    "to-light-theme": "Passer en thème clair",
    "to-dark-theme": "Passer en thème sombre",
    "change-language": "Changer de langue",
    "show-password": "Afficher le mot de passe",
    "hide-password": "Masquer le mot de passe",
    "main-navigation": "Navigation principale",
    breadcrumb: "Fil d’Ariane",
    home: "Revenir au tableau de bord",
    "open-navigation": "Ouvrir la navigation",
    "close-navigation": "Fermer la navigation",
    "collapse-sidebar": "Réduire la barre latérale",
    "expand-sidebar": "Déplier la barre latérale",
    "filter-reports": "Filtrer les signalements par statut",
    "search-accounts": "Rechercher un compte par e-mail ou pseudo",
    "sort-accounts": "Trier les comptes",
    "search-events": "Rechercher un événement par nom ou organisateur",
    "sort-events": "Trier les événements",
    "filter-events": "Filtrer les événements par statut",
    "filter-posts": "Filtrer les publications par statut",
    "search-posts": "Chercher une publication par texte ou par auteur",
    "filter-post-types": "Filtrer les publications par nature",
    "filter-deleted-posts": "Filtrer les publications retirées par leur auteur",
    "sort-posts": "Trier les publications",
    "post-photo": "Photo {{position}} de la publication",
    "reported-photo": "Photo {{position}} de la publication signalée",
    "clear-search": "Effacer la recherche",
    "chart-granularity": "Changer le pas de temps des graphiques",
  },
};
